import { type ReactNode, type Ref, useState } from "react";
import {
  Pressable,
  type PressableProps,
  StyleSheet,
  type StyleProp,
  type View,
  type ViewStyle,
} from "react-native";

import { useTheme } from "@/theme/useTheme";

import { Text } from "./Text";

export type PressableTargetProps = Omit<
  PressableProps,
  "children" | "style" | "accessibilityRole"
> & {
  accessibilityRole?: "button" | "link" | "tab" | "radio" | "menuitem";
  /** Required when the target has no text child (icon-only control, FR-044). */
  accessibilityLabel?: string;
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
  /** React 19 passes `ref` as a prop; it reaches the Pressable (focus management, 002 menus). */
  ref?: Ref<View>;
};

/**
 * The one pressable primitive: at least 44 x 44 (FR-044), a real accessible role, a label for
 * icon-only use, and the `focusRing` token outline when keyboard-focused on the web (FR-045).
 */
export function PressableTarget({
  accessibilityRole = "button",
  accessibilityLabel,
  children,
  style,
  onFocus,
  onBlur,
  ...rest
}: PressableTargetProps) {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  const isText = typeof children === "string" || typeof children === "number";

  if (!isText && !accessibilityLabel) {
    throw new Error(
      "PressableTarget needs an accessibilityLabel when it has no text child (FR-044)",
    );
  }

  return (
    <Pressable
      {...rest}
      accessibilityRole={accessibilityRole}
      accessibilityLabel={
        accessibilityLabel ?? (isText ? String(children) : undefined)
      }
      onFocus={(e) => {
        setFocused(true);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        onBlur?.(e);
      }}
      style={StyleSheet.flatten([
        {
          minWidth: theme.size.touchTarget,
          minHeight: theme.size.touchTarget,
          alignItems: "center",
          justifyContent: "center",
        },
        focused && {
          outlineColor: theme.color.focusRing,
          outlineWidth: theme.borderWidth.focusRing,
          outlineStyle: "solid",
          outlineOffset: theme.borderWidth.focusRing,
          borderRadius: theme.radius.button,
        },
        style,
      ])}
    >
      {isText ? <Text weight="semiBold">{children}</Text> : children}
    </Pressable>
  );
}
